// ═══════════════════════════════════════════════════════════════════════════════
// SYSTÈME D'ANIMATION UNIFIÉ — ESPACE PRO
// Standard unique pour toutes les pages : cohérence, précision, maîtrise
// ═══════════════════════════════════════════════════════════════════════════════

// ═══ DURÉE STANDARD ═══
// 0.4s partout — plus rapide = plus premium
export const DURATION = 0.4;

// ═══ EASING STANDARD ═══
export const EASE = [0.4, 0, 0.2, 1]; // cubic-bezier Apple/Stripe

// ═══ STAGGER DELAYS ═══
export const STAGGER = {
  title: 0,
  subtitle: 0.1,
  text: 0.15,
  buttons: 0.2,
  image: 0.25,
  extra: 0.3,
};

// ═══ STAGGER POUR LISTES ═══
export const LIST_STAGGER = 0.08; // index * 0.08

// ═══ VARIANTS FRAMER MOTION ═══

// Container pour stagger children
export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: LIST_STAGGER,
      delayChildren: 0.1,
    },
  },
};

// Item de liste standard
export const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: DURATION,
      ease: EASE,
    },
  },
};

// ═══ ANIMATIONS INDIVIDUELLES ═══

// Fade up standard (sections, blocs)
export const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: DURATION, ease: EASE, delay },
});

// Fade up pour Hero (animate au lieu de whileInView)
export const fadeUpHero = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: DURATION, ease: EASE, delay },
});

// Fade in simple (sans translation)
export const fadeIn = (delay = 0) => ({
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: DURATION, ease: EASE, delay },
});

// Fade from left
export const fadeLeft = (delay = 0) => ({
  initial: { opacity: 0, x: -20 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: DURATION, ease: EASE, delay },
});

// Fade from right
export const fadeRight = (delay = 0) => ({
  initial: { opacity: 0, x: 20 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: DURATION, ease: EASE, delay },
});

// Scale up (pour images, cards)
export const scaleUp = (delay = 0) => ({
  initial: { opacity: 0, scale: 0.95 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true },
  transition: { duration: DURATION, ease: EASE, delay },
});

// ═══ HERO SECTION STANDARD ═══
export const heroAnimations = {
  badge: fadeUpHero(STAGGER.title),
  title: fadeUpHero(STAGGER.subtitle),
  subtitle: fadeUpHero(STAGGER.text),
  buttons: fadeUpHero(STAGGER.buttons),
  image: fadeUpHero(STAGGER.image),
  extra: fadeUpHero(STAGGER.extra),
};

// ═══ SECTION STANDARD ═══
export const sectionAnimations = {
  badge: fadeUp(STAGGER.title),
  title: fadeUp(STAGGER.subtitle),
  subtitle: fadeUp(STAGGER.text),
  content: fadeUp(STAGGER.buttons),
  image: fadeUp(STAGGER.image),
};

// ═══ LISTE AVEC INDEX ═══
export const listItemAnimation = (index) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { 
    duration: DURATION, 
    ease: EASE, 
    delay: index * LIST_STAGGER,
  },
});

// ═══ STYLES CSS PERFORMANCE ═══
export const performanceStyles = {
  willChange: 'transform, opacity',
};

// ═══ COMPOSANT WRAPPER ANIMÉ ═══
// Usage: <AnimatedSection delay={0.1}>...</AnimatedSection>
export const AnimatedSectionProps = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: DURATION, ease: EASE, delay },
  style: { willChange: 'transform, opacity' },
});

// ═══ HELPER POUR LISTES ═══
// Usage: items.map((item, i) => <motion.div {...listItem(i)}>)
export const listItem = (index) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { 
    duration: DURATION, 
    ease: EASE, 
    delay: index * LIST_STAGGER,
  },
  style: { willChange: 'transform, opacity' },
});

export default {
  DURATION,
  EASE,
  STAGGER,
  LIST_STAGGER,
  containerVariants,
  itemVariants,
  fadeUp,
  fadeUpHero,
  fadeIn,
  fadeLeft,
  fadeRight,
  scaleUp,
  heroAnimations,
  sectionAnimations,
  listItemAnimation,
  listItem,
  performanceStyles,
  AnimatedSectionProps,
};
